import { Helmet } from 'react-helmet-async'
import { Link } from "../navigation";
import { Trans, useTranslation } from 'react-i18next'
import _ from 'lodash'

import { PlainImage } from '../components/Image'
import { useDesigns } from './DesignsProvider'
import { DesignCommodityLink, DesignFamily } from './designs'
import { designFamilyUrl } from './utils'
import { useFilteredDesigns } from '../filter/FilteredDesigns'
import { EmptyFilteredPortfolio, EmptyPortfolio } from './DesignPortfolio'
import { commodityUrl } from '../products/products'
import { useFilteredCommodities } from '../filter/FilteredCommodities'
import { useLocalState } from 'src/settings/localStorage';
import ToggleButton from 'src/components/ToggleButton';
import { design_family } from 'src/data';
import { ProductMappingMatrix } from '../products/ProductMappingMatrix'

export default function DesignProductMapping() {
    const { t } = useTranslation()

    const { designFamilies: _families } = useFilteredDesigns()
    const { designProductMappings, deleteDesignProductMapping, postDesignProductMapping, families: allFamilies } = useDesigns()
    const { commodities } = useFilteredCommodities()
    const [showPictures, setShowPictures] = useLocalState('design-pm-show-pictures', true)

    const families = _.sortBy(_families, 'reference')

    if (families.length === 0 || commodities.length === 0) {
        return <>
            {/* @ts-ignore */}
            <Helmet>
                <title>{t('product-mapping')} | {t('designs')} | Patent Cockpit</title>
            </Helmet>
            <div className="header-row">
                <div className="flex flex-row items-center max-w-2xl gap-2">
                    <h2 className="modern-h2 grow">{t('product-mapping')}</h2>
                </div>
            </div>
            <div className="main-content grow">
                {commodities.length === 0
                    ? <Trans
                        i18nKey="add-products-hint"
                        components={{ portfoliolink: <Link className="text-pcx-500 underline-link" to="/products/portfolio" /> }}
                    />
                    : allFamilies?.length > 0
                        ? <EmptyFilteredPortfolio />
                        : <EmptyPortfolio />}
            </div>
        </>
    }

    return <ProductMappingMatrix<DesignFamily, DesignCommodityLink>
        pageTitle={`${t('product-mapping')} | ${t('designs')} | Patent Cockpit`}
        heading={t('product-mapping')}
        families={families}
        commodities={commodities}
        links={designProductMappings}
        getFamilyId={f => f.designFamilyId}
        renderFamilyHeader={family => <>
            <Link className="underline-link whitespace-nowrap" to={designFamilyUrl(family)}>{family.reference}</Link>
            <p className="text-xs truncate">{family.name}</p>
            {showPictures &&
                <div className="max-w-[8rem] h-12 shrink-0">
                    <PlainImage
                        entity={design_family}
                        entityId={family.designFamilyId}
                        title={family.reference}
                        text={family.description}
                        clickable={false}
                    />
                </div>}
        </>}
        renderCommodityCell={commodity => <>
            <Link
                className="underline-link block pl-3 bg-white dark:bg-pcx-100"
                to={commodityUrl(commodity)}
            >{commodity.commodityReference}</Link>
            {showPictures &&
                <div className="w-28 h-12">
                    <PlainImage
                        entity="commodity"
                        entityId={commodity.commodityId}
                        clickable={false}
                    />
                </div>}
        </>}
        baseLinkFor={(commodityId, family) => ({ commodityId, familyId: family.designFamilyId })}
        isEqualLink={(a, b) => a.commodityId === b.commodityId && a.familyId === b.familyId}
        postLinks={postDesignProductMapping}
        deleteLinks={deleteDesignProductMapping}
        headerExtras={
            <label className="flex flex-row items-center gap-2">
                <span>{t('show-pictures')}</span>
                <ToggleButton checked={showPictures} setChecked={setShowPictures} />
            </label>
        }
    />
}
